import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { createContact, getMyContacts, getStaffUsers, getAdminAudience, markAudienceRead, approveUStorePurchase, rejectUStorePurchase, uploadContactAttachment } from '@/lib/api';
import { Send } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { getImageFileFromClipboard } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';
import { getInputSurface, getPanelStyle, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

export function DMPage() {
    const [category, setCategory] = useState('General');
    const [message, setMessage] = useState('');
    const messageRef = useRef<HTMLTextAreaElement>(null);
    const [recipientId, setRecipientId] = useState<number | undefined>(undefined);
    const [recipientLabel, setRecipientLabel] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [attachments, setAttachments] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [attachmentError, setAttachmentError] = useState('');

    const [staff, setStaff] = useState<any[]>([]);
    const [history, setHistory] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'write' | 'history'>('write');
    const [filterUsername, setFilterUsername] = useState('');
    const [filterEmail, setFilterEmail] = useState('');

    // @ts-ignore
    const { theme, user } = useOutletContext<{ theme: any, user: any }>();
    const darkTheme = isDarkTheme(theme);
    const panelStyle = getPanelStyle(theme, 0.96, 0.9);
    const inputSurface = getInputSurface(theme);

    useEffect(() => {
        if (!user) return;
        fetchStaff();
        fetchHistory();
    }, [user]);

    useEffect(() => {
        autoResizeTextarea(messageRef.current);
    }, [message]);

    const resolveSenderName = (item: any) => {
        const senderName = (item.sender_name || '').trim();
        const senderUsername = (item.sender_username || '').trim();
        if (senderName && senderName !== senderUsername) return senderName;
        return '住人';
    };

    const resolveRecipientName = (item: any) => {
        const recipientName = (item.recipient_name || '').trim();
        const recipientUsername = (item.recipient_username || '').trim();
        if (recipientName && recipientName !== recipientUsername) return recipientName;
        return '住人';
    };

    const fetchStaff = async () => {
        try {
            const res = await getStaffUsers();
            const filtered = res.data.filter((s: any) => ['Admin', 'Maestro'].includes(s.role_type));
            setStaff(filtered);
            // Default select top admin if available
            const admin = filtered.find((s: any) => s.role_type === 'Admin');
            if (admin) setRecipientId(admin.user_id);
        } catch (e) {
            console.error("Failed to fetch staff");
        }
    };

    const fetchHistory = async () => {
        try {
            if (user.role_type === 'Admin' || user.role_type === 'Maestro') {
                // Admin sees all, Maestro sees only messages addressed to them
                const res = await getAdminAudience();
                setHistory(res.data);
            } else {
                // Residents (Floor10) see their own sent/received
                const res = await getMyContacts('audience');
                setHistory(res.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const filteredHistory = history.filter((item) => {
        const usernameQuery = filterUsername.trim().toLowerCase();
        const emailQuery = filterEmail.trim().toLowerCase();

        const name = resolveSenderName(item).toLowerCase();
        const email = (item.sender_email || '').toLowerCase();

        if (usernameQuery && !name.includes(usernameQuery)) return false;
        if (emailQuery && !email.includes(emailQuery)) return false;
        return true;
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!message.trim()) return;
        if (!recipientId) {
            if (user?.role_type === 'Admin' || user?.role_type === 'Maestro') {
                setError('返信先が未選択です。拝謁の記録から返信先を選択してください。');
            } else {
                setError('謁見相手を選択してください。');
            }
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await createContact(category, message, recipientId, undefined, attachments);
            setMessage('');
            setAttachments([]);
            fetchHistory(); // Refresh history
            setActiveTab('history');
        } catch (err) {
            console.error(err);
            setError('送信に失敗しました。');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setIsUploading(true);
        setAttachmentError('');
        try {
            const uploaded: string[] = [];
            for (const file of files) {
                if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                    continue;
                }
                const res = await uploadContactAttachment(file);
                if (res.data?.url) {
                    uploaded.push(res.data.url);
                }
            }
            if (uploaded.length) {
                setAttachments((prev) => [...prev, ...uploaded]);
            }
        } catch (err) {
            console.error(err);
            setAttachmentError('添付のアップロードに失敗しました。');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handlePasteAttachment = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        setIsUploading(true);
        setAttachmentError('');
        try {
            const res = await uploadContactAttachment(imageFile);
            if (res.data?.url) {
                setAttachments((prev) => [...prev, res.data.url]);
            }
        } catch (err) {
            console.error(err);
            setAttachmentError('添付のアップロードに失敗しました。');
        } finally {
            setIsUploading(false);
        }
    };

    const removeAttachment = (url: string) => {
        setAttachments((prev) => prev.filter((item) => item !== url));
    };

    const handleSelectReply = async (item: any) => {
        if (!user) return;
        if (item.meta?.kind === 'ustore_purchase') return;
        if (!item.user_id || item.user_id === user.user_id) return;

        const label = resolveSenderName(item);
        setRecipientId(item.user_id);
        setRecipientLabel(label);

        const allowedCategories = ['General', 'Appeal', 'Report', 'Greeting'];
        setCategory(allowedCategories.includes(item.category) ? item.category : 'General');

        setActiveTab('write');
        await handleMarkRead(item);
    };

    const handleMarkRead = async (item: any) => {
        if (!item.is_read) {
            try {
                await markAudienceRead(item.contact_id);
                setHistory((prev) =>
                    prev.map((i) => (i.contact_id === item.contact_id ? { ...i, is_read: true } : i))
                );
            } catch (e) {
                console.error('Failed to mark as read', e);
            }
        }
    };

    const handleApprovePurchase = async (item: any) => {
        try {
            await approveUStorePurchase(item.contact_id);
            fetchHistory();
        } catch (e: any) {
            console.error(e);
            setError(e?.response?.data?.detail || '承認に失敗しました。');
        }
    };

    const handleRejectPurchase = async (item: any) => {
        try {
            await rejectUStorePurchase(item.contact_id);
            fetchHistory();
        } catch (e: any) {
            console.error(e);
            setError(e?.response?.data?.detail || '却下に失敗しました。');
        }
    };

    if (!user) {
        return (
            <div className="max-w-4xl mx-auto mt-8 p-4 text-center opacity-70">
                Loading...
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto mt-8 p-4">
            <h1 className="text-3xl font-serif font-bold mb-6 tracking-widest text-center" style={{ color: theme?.colors?.text }}>
                謁見の間
            </h1>
            <p className="text-center opacity-70 mb-8 max-w-2xl mx-auto font-serif">
                選ばれし者のみが入室を許される特別な空間。<br />
                Signore、またはMaestroへの直接の言葉をお伝えください。
            </p>

            {/* Tabs */}
            <div className="flex justify-center gap-8 mb-8 border-b border-opacity-20" style={{ borderColor: theme?.colors?.border }}>
                <button
                    onClick={() => setActiveTab('write')}
                    className={`pb-2 px-8 font-serif font-bold transition-all duration-300 tracking-wider ${activeTab === 'write' ? 'border-b-2 opacity-100' : 'opacity-40 hover:opacity-70'}`}
                    style={{
                        borderColor: activeTab === 'write' ? theme?.colors?.primary : 'transparent',
                        color: theme?.colors?.text
                    }}
                >
                    上奏
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`pb-2 px-8 font-serif font-bold transition-all duration-300 tracking-wider ${activeTab === 'history' ? 'border-b-2 opacity-100' : 'opacity-40 hover:opacity-70'}`}
                    style={{
                        borderColor: activeTab === 'history' ? theme?.colors?.primary : 'transparent',
                        color: theme?.colors?.text
                    }}
                >
                    拝謁の記録
                </button>
            </div>

            {activeTab === 'write' ? (
                <form onSubmit={handleSubmit} className=" backdrop-blur-md p-8 rounded-xl shadow-lg border"
                    style={panelStyle}>
                    <div className="space-y-6">
                        {(user.role_type === 'Admin' || user.role_type === 'Maestro') ? (
                            <div>
                                <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>返信先</label>
                                <div
                                    className="w-full p-3 border rounded-lg font-serif text-sm opacity-80"
                                    style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                                >
                                    {recipientLabel || '拝謁の記録から返信先を選択してください'}
                                </div>
                                {recipientLabel && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setRecipientId(undefined);
                                            setRecipientLabel('');
                                        }}
                                        className="mt-2 text-xs opacity-70 hover:opacity-100"
                                        style={{ color: theme?.colors?.text }}
                                    >
                                        返信先をクリア
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>謁見相手を選択</label>
                                <select
                                    value={recipientId || ''}
                                    onChange={(e) => setRecipientId(e.target.value ? Number(e.target.value) : undefined)}
                                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 font-serif"
                                    style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                                >
                                    <option value="">選択してください</option>
                                    {staff.map(s => (
                                        <option key={s.user_id} value={s.user_id}>
                                            {s.role_type === 'Admin' ? 'Signore (Admin)' : `${s.role_type}: ${s.display_name?.trim() || '住人'}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>件名・区分</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2"
                                style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                            >
                                <option value="General">一般的な上奏</option>
                                <option value="Appeal">嘆願</option>
                                <option value="Report">報告</option>
                                <option value="Greeting">挨拶</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>上奏文</label>
                            <textarea
                                ref={messageRef}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                onPaste={handlePasteAttachment}
                                rows={6}
                                required
                                className="w-full p-4 border rounded-lg focus:outline-none focus:ring-2 resize-none"
                                placeholder="ここに入力..."
                                style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>
                                添付（任意・画像/動画）
                            </label>
                            <div className="flex items-center gap-3">
                                <label
                                    htmlFor="audience-attachment"
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer"
                                    style={{ borderColor: theme?.colors?.border, color: theme?.colors?.text }}
                                >
                                    追加する
                                </label>
                                <input
                                    id="audience-attachment"
                                    type="file"
                                    multiple
                                    accept="image/*,video/*"
                                    onChange={handleAttachmentChange}
                                    className="hidden"
                                />
                                {isUploading && <span className="text-xs opacity-70">アップロード中...</span>}
                            </div>
                            {attachmentError && <div className="text-red-500 text-xs mt-2">{attachmentError}</div>}
                            {attachments.length > 0 && (
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {attachments.map((url) => (
                                        <div key={url} className="relative border rounded-lg overflow-hidden" style={{ borderColor: theme?.colors?.border }}>
                                            {/\.(mp4|webm|mov|m4v)$/i.test(url.split('?')[0]) ? (
                                                <video src={url} controls className="w-full max-h-40 bg-black" />
                                            ) : (
                                                <img src={url} alt="Attachment" className="w-full max-h-40 object-cover" />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeAttachment(url)}
                                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {error && <div className="text-red-500 text-sm">{error}</div>}

                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                disabled={isSubmitting || !message.trim()}
                                className="w-full sm:w-auto gap-2 font-serif tracking-widest"
                                style={{ backgroundColor: theme?.colors?.primary, color: pickTextColor(theme?.colors?.primary || '#000000') }}
                            >
                                {isSubmitting ? '送信中...' : '上奏する'}
                                <Send size={16} />
                            </Button>
                        </div>
                    </div>
                </form>
            ) : (
                <div className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-2 mb-4">
                        <input
                            type="text"
                            value={filterUsername}
                            onChange={(e) => setFilterUsername(e.target.value)}
                            className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2"
                            placeholder="表示名で絞り込み"
                            style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                        />
                        <input
                            type="text"
                            value={filterEmail}
                            onChange={(e) => setFilterEmail(e.target.value)}
                            className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2"
                            placeholder="メールアドレスで絞り込み"
                            style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                        />
                    </div>
                    {filteredHistory.length === 0 ? (
                        <p className="text-center opacity-50">拝謁の記録はありません。</p>
                    ) : (
                        filteredHistory.map(item => (
                            <div
                                key={item.contact_id}
                                onClick={() => handleMarkRead(item)}
                                className="p-4 rounded-xl border backdrop-blur-sm relative cursor-pointer"
                                style={{
                                    backgroundColor: withAlpha(theme?.colors?.card || '#ffffff', darkTheme ? 0.84 : 0.76),
                                    borderColor: theme?.colors?.border,
                                }}>
                                <div className="flex justify-between items-start mb-2">
                                    <div className="font-bold flex items-center gap-2" style={{ color: theme?.colors?.primary }}>
                                        {/* Display logic for Sender/Recipient */}
                                        <span className="text-sm">
                                            {item.sender_name ? `${resolveSenderName(item)} → ` : 'Me → '}
                                            {resolveRecipientName(item)}
                                        </span>
                                        <span className="text-xs opacity-50 font-normal">
                                            {new Date(item.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    {item.is_resolved && (
                                        <span
                                            className="text-xs px-2 py-1 rounded-full border"
                                            style={{
                                                backgroundColor: withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.18 : 0.12),
                                                color: theme?.colors?.primary,
                                                borderColor: withAlpha(theme?.colors?.primary || '#000000', 0.4),
                                            }}
                                        >
                                            Replied/Resolved
                                        </span>
                                    )}
                                </div>
                                <div className="font-medium text-sm mb-1 opacity-80">{item.category}</div>
                                {item.meta?.kind === 'ustore_purchase' && (
                                    <div
                                        className="text-xs mb-2 px-2 py-1 rounded inline-block border"
                                        style={{
                                            color: theme?.colors?.primary,
                                            backgroundColor: withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.14 : 0.1),
                                            borderColor: withAlpha(theme?.colors?.primary || '#000000', 0.35),
                                        }}
                                    >
                                        U-Store購入申請
                                    </div>
                                )}
                                {item.meta?.kind === 'ustore_approved' && (
                                    <div
                                        className="text-xs mb-2 px-2 py-1 rounded inline-block border"
                                        style={{
                                            color: theme?.colors?.primary,
                                            backgroundColor: withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.14 : 0.1),
                                            borderColor: withAlpha(theme?.colors?.primary || '#000000', 0.35),
                                        }}
                                    >
                                        U-Store購入承認
                                    </div>
                                )}
                                {item.meta?.kind === 'ustore_rejected' && (
                                    <div
                                        className="text-xs mb-2 px-2 py-1 rounded inline-block border"
                                        style={{
                                            color: '#ef4444',
                                            backgroundColor: withAlpha('#ef4444', darkTheme ? 0.14 : 0.08),
                                            borderColor: withAlpha('#ef4444', 0.35),
                                        }}
                                    >
                                        U-Store購入却下
                                    </div>
                                )}
                                <div className="text-sm whitespace-pre-wrap">{item.message}</div>
                                {item.attachment_urls && item.attachment_urls.length > 0 && (
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {item.attachment_urls.map((url: string) => (
                                            <div key={url} className="border rounded-lg overflow-hidden" style={{ borderColor: theme?.colors?.border }}>
                                                {/\.(mp4|webm|mov|m4v)$/i.test(url.split('?')[0]) ? (
                                                    <video src={url} controls className="w-full max-h-40 bg-black" />
                                                ) : (
                                                    <img src={url} alt="Attachment" className="w-full max-h-40 object-cover" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {(user.role_type === 'Admin' || user.role_type === 'Maestro') && item.meta?.kind === 'ustore_purchase' && !item.is_resolved && (
                                    <div className="mt-3 flex justify-end gap-2">
                                        <Button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRejectPurchase(item);
                                            }}
                                            className="gap-2 font-serif tracking-widest"
                                            style={{ backgroundColor: withAlpha('#6b7280', 0.95), color: '#ffffff' }}
                                        >
                                            購入を却下
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleApprovePurchase(item);
                                            }}
                                            className="gap-2 font-serif tracking-widest"
                                            style={{ backgroundColor: theme?.colors?.primary, color: pickTextColor(theme?.colors?.primary || '#000000') }}
                                        >
                                            購入を承認
                                        </Button>
                                    </div>
                                )}
                                {(user.role_type === 'Admin' || user.role_type === 'Maestro') && item.user_id && item.user_id !== user.user_id && item.meta?.kind !== 'ustore_purchase' && (
                                    <div className="mt-3 flex justify-end">
                                        <Button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelectReply(item);
                                            }}
                                            className="gap-2 font-serif tracking-widest"
                                            style={{ backgroundColor: theme?.colors?.primary, color: pickTextColor(theme?.colors?.primary || '#000000') }}
                                        >
                                            返信する
                                            <Send size={14} />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
